import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectors, utils } from 'state';
import { formatters } from 'services';
import { Card, Table, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { OptionsContractType } from '../../../state/app/options/types';

interface OptionsChainTableProps {
  asset_id: number;
  expiration: string;
}

interface OptionsChainRow {
  strike: number;
  call?: OptionsContractType;
  put?: OptionsContractType;
}
interface CurrentPosition {
  amount: number;
}

const CurrentSpotPosition: React.FC<OptionsChainTableProps> = ({
  asset_id,
  expiration,
}) => {
  const dispatch = useDispatch();
  const optionsContracts: OptionsContractType[] =
    useSelector(selectors.assetOptionsContracts(asset_id)) || [];
  const underlyingSpot = useSelector(selectors.underlyingSpot(asset_id));
  const { scenario_date, id } = useSelector(selectors.activeSession);

  const optionsAsset = useSelector(selectors.underlyingAssetById(asset_id));
  const underlyingAsset = useSelector(
    selectors.underlyingAssetById(optionsAsset.non_derivate_asset)
  );
  const spotFin = useSelector(
    selectors.financialInstrumentByUnderlingAsset(underlyingAsset.id)
  );
  const currencies = useSelector(selectors.currencies);
  const assetList = useSelector(selectors.underlyingAssetsList);
  const currencyList = useSelector(selectors.currenciesList);
  const reportingCurrency = utils.useReportingCurrency(id);

  const reportingValue = utils.reportingValue(
    spotFin as any,
    currencies[underlyingAsset.base_currency],
    underlyingAsset.resourcetype,
    assetList,
    currencyList,
    reportingCurrency
  );

  if (spotFin === undefined) return <div></div>;

  const data = [
    {
      amount: spotFin.amount,
      open: spotFin.avg_open_price,
      bid: underlyingAsset.bid,
      ask: underlyingAsset.ask,
      reporting_unrealized_pnl: spotFin.reporting_unrealized_pnl,
      reporting_realized_pnl: spotFin.reporting_realized_pnl,
      reporting_value: reportingValue,
    },
  ];
  const columns: ColumnsType<CurrentPosition> = [
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: formatters.commasNoDigits,
      align: 'right',
    },
    {
      title: 'Open',
      dataIndex: 'open',
      key: 'open',
      render: formatters.commas2Digits,
      align: 'right',
    },
    {
      title: 'Bid',
      dataIndex: 'bid',
      key: 'bid',
      render: formatters.commas2Digits,
      align: 'right',
    },
    {
      title: 'Ask',
      dataIndex: 'ask',
      key: 'ask',
      render: formatters.commas2Digits,
      align: 'right',
    },
    {
      title: 'Unrealized, ' + reportingCurrency.symbol,
      dataIndex: 'reporting_unrealized_pnl',
      align: 'right',
      render: formatters.commasNoDigits,
    },
    {
      title: 'Realized, ' + reportingCurrency.symbol,
      dataIndex: 'reporting_realized_pnl',
      align: 'right',
      render: formatters.commasNoDigits,
    },
    {
      title: 'Value, ' + reportingCurrency.symbol,
      // for bonds this is clean open value (amount * clean open price)
      dataIndex: 'reporting_value',
      key: 'reporting_value',
      align: 'right',
      render: formatters.commasNoDigits,
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>
        {underlyingAsset.ticker} Spot Position
      </Typography.Title>

      {/*<div style={{ height: '250px' }}>hi.</div>*/}
      <Table dataSource={data} columns={columns} bordered pagination={false} />
    </div>
  );
};

export default CurrentSpotPosition;
